import { useMemo, useState } from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import type { NeedSummary, Quote, RequestQuotesResult, VendorWithRelations } from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { SearchableListSelect } from '../common/SearchableListSelect';
import { MessageComposer } from '../messaging/MessageComposer';
import { useVendors } from '../../hooks/useVendors';
import { useRequestQuotes } from '../../hooks/useNeeds';
import { QuickVendorForm } from './QuickVendorForm';
import { extractApiErrorMessage } from '../../utils/apiError';

export interface RequestQuotesSheetProps {
  open: boolean;
  eventId: string;
  eventName: string;
  need: NeedSummary;
  /** The need's quotes, so a vendor already asked is marked. */
  quotes: Quote[];
  onClose: () => void;
}

function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

/**
 * Ask vendors for a quote: each gets the producer's letter, the need's items
 * as a table, and a single-use link to upload their quote; replies come back
 * into that vendor's conversation. Vendors are offered from the need's
 * category first, and a new one can be added without leaving.
 */
export function RequestQuotesSheet(props: RequestQuotesSheetProps) {
  return (
    <EntityModal open={props.open} onClose={props.onClose} title="Ask for quotes" size="lg">
      {props.open && <RequestForm key={props.need.id} {...props} />}
    </EntityModal>
  );
}

function RequestForm({ eventId, eventName, need, quotes, onClose }: RequestQuotesSheetProps) {
  const [allVendors, setAllVendors] = useState(false);
  const { data: vendors = [], isLoading } = useVendors({
    isActive: true,
    ...(allVendors ? {} : { vendorCategoryId: need.vendorCategoryId }),
  });
  const request = useRequestQuotes(need.id);
  const [added, setAdded] = useState<VendorWithRelations[]>([]);
  const [chosen, setChosen] = useState<string[]>([]);
  const [newVendor, setNewVendor] = useState(false);
  const name = need.itemName || need.vendorCategoryName;
  const [subject, setSubject] = useState(`Quote request: ${name} — ${eventName}`);
  const [bodyHtml, setBodyHtml] = useState(
    `<p>Hello,</p><p>We are planning ${escapeHtml(eventName)} and would like your quote for ${escapeHtml(
      name,
    )}.</p><p>Thank you!</p>`,
  );
  const [includeItems, setIncludeItems] = useState(true);
  const [ccSelf, setCcSelf] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<RequestQuotesResult | null>(null);

  const asked = useMemo(() => new Map(quotes.map((q) => [q.vendorId, q.status])), [quotes]);
  const options = useMemo(() => {
    const seen = new Set<string>();
    return [...added, ...vendors].filter((v) => !seen.has(v.id) && seen.add(v.id));
  }, [added, vendors]);
  const withoutEmail = options.filter((v) => chosen.includes(v.id) && !v.email);

  const send = async () => {
    setError('');
    if (chosen.length === 0) return setError('Choose at least one vendor.');
    if (!subject.trim()) return setError('Write a subject.');
    try {
      setResult(
        await request.mutateAsync({
          vendorIds: chosen,
          subject: subject.trim(),
          bodyHtml,
          includeItems: includeItems && need.itemCount > 0,
          ccSelf,
        }),
      );
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Could not send the requests'));
    }
  };

  if (result) {
    return (
      <div className="space-y-4">
        {result.sent.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-1">Asked</h3>
            <ul className="space-y-1">
              {result.sent.map((s) => (
                <li key={s.vendorId} className="flex items-center gap-2 text-sm text-gray-700">
                  <CheckCircle2 size={16} className="text-green-600 shrink-0" />
                  {s.vendorName} <span className="text-gray-400">· {s.to}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {result.skipped.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-1">Not asked</h3>
            <ul className="space-y-1">
              {result.skipped.map((s) => (
                <li key={s.vendorId} className="flex items-center gap-2 text-sm text-gray-700">
                  <XCircle size={16} className="text-red-600 shrink-0" />
                  {s.vendorName} <span className="text-gray-500">— {s.reason}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="min-h-11 sm:min-h-9 px-4 bg-primary-600 text-white text-sm font-semibold rounded-lg hover:bg-primary-700"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div role="alert" className="px-3 py-2 bg-red-50 text-red-700 text-sm rounded">
          {error}
        </div>
      )}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label
            htmlFor="request-quotes-vendors"
            className="block text-sm font-medium text-gray-700"
          >
            Vendors
          </label>
          <label className="flex items-center gap-2 text-xs text-gray-600 min-h-11 sm:min-h-0">
            <input
              type="checkbox"
              checked={allVendors}
              onChange={(e) => setAllVendors(e.target.checked)}
            />
            Every category
          </label>
        </div>
        <SearchableListSelect
          inputId="request-quotes-vendors"
          multiple
          items={options}
          value={chosen}
          onChange={(v) => setChosen(v as string[])}
          getValue={(v) => v.id}
          getLabel={(v) => v.businessName}
          getSubtitle={(v) => {
            const status = asked.get(v.id);
            const parts = [
              v.email ?? 'No email',
              v.rating ? `★ ${v.rating}` : null,
              status ? `Already asked (${status.toLowerCase()})` : null,
            ];
            return parts.filter(Boolean).join(' · ');
          }}
          isLoading={isLoading}
          placeholder={
            allVendors ? 'Search all vendors' : `Search ${need.vendorCategoryName} vendors`
          }
          emptyMessage={allVendors ? 'No vendors yet' : `No ${need.vendorCategoryName} vendors yet`}
          ariaLabel="Vendors to ask"
        />
        {withoutEmail.length > 0 && (
          <p className="mt-1 text-xs text-amber-700">
            {withoutEmail.map((v) => v.businessName).join(', ')}{' '}
            {withoutEmail.length === 1 ? 'has' : 'have'} no email and will be skipped.
          </p>
        )}
        {!newVendor && (
          <button
            type="button"
            onClick={() => setNewVendor(true)}
            className="mt-1 min-h-11 sm:min-h-0 text-sm text-primary-700 hover:underline"
          >
            + New vendor
          </button>
        )}
      </div>
      {newVendor && (
        <QuickVendorForm
          defaultCategoryId={need.vendorCategoryId}
          onCancel={() => setNewVendor(false)}
          onCreated={(vendor) => {
            setAdded((list) => [vendor, ...list]);
            setChosen((ids) => [...ids, vendor.id]);
            setNewVendor(false);
          }}
        />
      )}
      <div>
        <label
          htmlFor="request-quotes-subject"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Subject
        </label>
        <input
          id="request-quotes-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          maxLength={300}
          className="w-full h-11 sm:h-10 px-3 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      <MessageComposer
        id="request-quotes-body"
        format="html"
        label="Message"
        ariaLabel="Request body"
        value={bodyHtml}
        onChange={setBodyHtml}
        record={{ entityType: 'Event', entityId: eventId }}
        subject={subject}
        onSubjectChange={setSubject}
        onError={setError}
        disabled={request.isPending}
      />
      <div className="space-y-1">
        <label className="flex items-center gap-2 text-sm text-gray-700 min-h-11 sm:min-h-0">
          <input
            type="checkbox"
            checked={includeItems && need.itemCount > 0}
            disabled={need.itemCount === 0}
            onChange={(e) => setIncludeItems(e.target.checked)}
          />
          {need.itemCount > 0
            ? `Include the ${need.itemCount} ${need.itemCount === 1 ? 'item' : 'items'} as a table`
            : 'No items to include yet — add them on the Items tab'}
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700 min-h-11 sm:min-h-0">
          <input type="checkbox" checked={ccSelf} onChange={(e) => setCcSelf(e.target.checked)} />
          Send me a copy
        </label>
        <p className="text-xs text-gray-500">
          Each vendor gets a link to upload their quote, and replies come back into their
          conversation.
        </p>
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="min-h-11 sm:min-h-9 px-4 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void send()}
          disabled={request.isPending || chosen.length === 0}
          className="min-h-11 sm:min-h-9 px-4 bg-primary-600 text-white text-sm font-semibold rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {request.isPending
            ? 'Sending…'
            : chosen.length === 0
              ? 'Ask vendors'
              : `Ask ${chosen.length} ${chosen.length === 1 ? 'vendor' : 'vendors'}`}
        </button>
      </div>
    </div>
  );
}
